import { stringify } from 'qs';

import type { AsyncDataReloaderT } from '@dr.pogodin/react-global-state';
import { time } from '@dr.pogodin/react-utils';

import { listBlogItems } from 'services/blog/list';

import {
  type BlogItemQueryT,
  type BlogItemT,
  type BlogItemsListT,
  DEFAULT_BLOG_PAGE_SIZE,
  getId,
  useAsyncData,
} from 'utils';

type UseBlogItemListArgT = {
  page?: number;
  pageSize?: number;
  query?: BlogItemQueryT;
};

type UseBlogItemListResT = {
  items: BlogItemT[];
  loadMore: (() => void) | undefined;
  loading: boolean;
  numPages: number | undefined;
  reload: AsyncDataReloaderT<BlogItemsListT>;
};

export default function useBlogItemList({
  page,
  pageSize = DEFAULT_BLOG_PAGE_SIZE,
  query,
}: UseBlogItemListArgT): UseBlogItemListResT {
  const queryString: string = stringify(query);

  const e = useAsyncData('blog.list', async (old): Promise<BlogItemsListT> => {
    if (
      old
      && (page === undefined || (
        page === old.lastPage
        && old.items.length === old.pageSize
      ))
      && pageSize === old.pageSize
      && queryString === old.queryString
    ) return old;

    return (async (): Promise<BlogItemsListT> => {
      const res = await listBlogItems({
        page,
        pageSize,
        withTotalCount: page !== undefined,
        ...query,
      });

      // TODO: Cross-update items in the blog map?

      return {
        hasMore: res.items.length === pageSize,
        items: res.items,
        lastPage: page ?? 0,
        numPages: res.totalCount && Math.ceil(res.totalCount / pageSize),
        pageSize,
        queryString,
      };
    })();
  }, {
    deps: [page, pageSize, queryString],
    maxage: time.YEAR_MS,
  });

  return {
    items: e.data?.items ?? [],
    loadMore: e.data?.hasMore && !e.loading ? () => {
      void e.reload(async (old): Promise<BlogItemsListT> => {
        if (!old) throw Error('Internal error');

        const res = await listBlogItems({
          page: old.lastPage + 1,
          pageSize,
          ...query,
        });

        const items = [...old.items];
        const itemMap: Record<string, number> = {};
        for (let idx = 0; idx < items.length; ++idx) {
          itemMap[getId(items[idx])] = idx;
        }

        for (const item of res.items) {
          const itemId = getId(item);
          const idx = itemMap[itemId];
          if (idx === undefined) items.push(item);
          else items[idx] = item;
        }

        return {
          hasMore: res.items.length === pageSize,
          items,
          lastPage: old.lastPage + 1,
          numPages: res.totalCount && Math.ceil(res.totalCount / pageSize),
          pageSize,
          queryString,
        };
      });
    } : undefined,
    loading: e.loading,
    numPages: e.data?.numPages,
    reload: e.reload,
  };
}
